import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/** Server-paged list that keeps loading as you scroll.

    A full page back means there may be more, so the client never needs a total count — which is
    what lets a company with ten thousand work orders open the page as fast as a brand new one. */
export function useInfiniteList(path, { params = {}, pageSize = 100 } = {}) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  // Params are compared by value, so a caller may pass a fresh object each render safely.
  const key = JSON.stringify(params);
  const inFlight = useRef(false);

  const fetchPage = useCallback(async (which, replace) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { data } = await api.get(path, {
        params: { ...params, page: which, page_size: pageSize },
      });
      const rows = Array.isArray(data) ? data : [];
      setItems((prev) => (replace ? rows : [...prev, ...rows]));
      setHasMore(rows.length === pageSize);
      setPage(which);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, pageSize]);

  useEffect(() => {
    fetchPage(1, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    fetchPage(page + 1, false);
  }, [fetchPage, hasMore, loading, page]);

  const reload = useCallback(() => fetchPage(1, true), [fetchPage]);

  return { items, setItems, loadMore, hasMore, loading, reload };
}

/** Drop this at the bottom of a list: it asks for the next page as it comes into view. */
export function useAutoLoad(loadMore, hasMore) {
  const sentinel = useRef(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && loadMore(),
      { rootMargin: "400px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore, hasMore]);
  return sentinel;
}
